import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { jobService } from "../services/jobService";
import {
  Upload,
  Download,
  Square,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import * as XLSX from "xlsx";
import { fieldService } from "../services/fieldService";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Checkbox } from "../components/ui/checkbox";
import toast from "react-hot-toast";
import { Skeleton } from "../components/common/Skeleton";
import TableSkeleton from "../components/skeletons/TableSkeleton";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";

const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = [
  "COMPLETED",
  "FAILED",
  "PARTIAL_SUCCESS",
  "CANCELLED",
  "CANCELLING",
];
const CANCELLABLE_STATUSES = ["PENDING", "PROCESSING"];

/** Allowed import column mapping targets (staging parity). */
const IMPORT_TARGET_FIELDS = [
  "Ignore",
  "Title",
  "Priority",
  "Status",
  "Section",
  "Suite",
  "Preconditions",
  "Expected Result",
  "Approver",
  "Attachments",
  "Automation Status",
  "Assign To",
  "Owner",
  "Requirements",
  "State",
  "Tags",
  "Type of Test Case",
  "Description",
  "Steps",
  "BDD Steps",
  ...Array.from({ length: 20 }, (_, idx) => `Step ${idx + 1}`),
  ...Array.from({ length: 20 }, (_, idx) => `Expected Result ${idx + 1}`),
];

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleString();
}

function processingTime(startedAt, completedAt) {
  if (!startedAt || !completedAt) return "—";
  const ms = new Date(completedAt) - new Date(startedAt);
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const STATUS_CONFIG = {
  COMPLETED: {
    label: "Completed",
    bg: "bg-green-100",
    text: "text-green-700",
    icon: CheckCircle2,
  },
  PARTIAL_SUCCESS: {
    label: "Partial",
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: AlertCircle,
  },
  FAILED: {
    label: "Failed",
    bg: "bg-red-100",
    text: "text-red-700",
    icon: XCircle,
  },
  CANCELLED: {
    label: "Cancelled",
    bg: "bg-gray-100",
    text: "text-gray-600",
    icon: Square,
  },
  CANCELLING: {
    label: "Cancelling…",
    bg: "bg-gray-100",
    text: "text-gray-600",
    icon: Clock,
  },
  PENDING: {
    label: "Pending",
    bg: "bg-blue-100",
    text: "text-blue-700",
    icon: Clock,
  },
  PROCESSING: {
    label: "Processing",
    bg: "bg-indigo-100",
    text: "text-indigo-700",
    icon: Clock,
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || {
    label: status,
    bg: "bg-gray-100",
    text: "text-gray-600",
    icon: Clock,
  };
  const Icon = cfg.icon;
  const spin = status === "PROCESSING" || status === "CANCELLING";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      {spin ? (
        <Skeleton className="h-3 w-3 shrink-0 rounded-full" />
      ) : (
        <Icon className="w-3 h-3" />
      )}
      {cfg.label}
    </span>
  );
}

export default function DataManagementPage() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabParam === "export" ? "export" : "import",
  );

  const [importJobs, setImportJobs] = useState([]);
  const [fields,setFields]=useState(IMPORT_TARGET_FIELDS)
  const [importLoading, setImportLoading] = useState(false);
  const [importStartLoading, setImportStartLoading] = useState(false);
  const [importSampleLoading, setImportSampleLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [uploadedHeaders, setUploadedHeaders] = useState([]);
  const [headerMappings, setHeaderMappings] = useState({});

  const [exportJobs, setExportJobs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStartLoading, setExportStartLoading] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [includeRejected, setIncludeRejected] = useState(false);
  const [cancelingJobIds, setCancelingJobIds] = useState(() => new Set());

  const pollingJobIds = useRef(new Set());
  const fileInputRef = useRef(null);

  const loadImportJobs = useCallback(
    async (silent = false) => {
      if (!projectId) return;
      if (!silent) setImportLoading(true);
      try {
        const res = await jobService.listImportJobs({
          project_id: projectId,
          page: 1,
          limit: 50,
        });
        const data = res.data || res;
        const jobs = data.jobs || [];
        setImportJobs(jobs);
        jobs.forEach((j) => {
          if (j.job_id && !TERMINAL_STATUSES.includes(j.status))
            pollingJobIds.current.add(j.job_id);
        });
      } catch (e) {
        if (!silent)
          toast.error(
            e.response?.data?.message || "Failed to load import jobs",
          );
      } finally {
        if (!silent) setImportLoading(false);
      }
    },
    [projectId],
  );

  const loadExportJobs = useCallback(
    async (silent = false) => {
      if (!projectId) return;
      if (!silent) setExportLoading(true);
      try {
        const res = await jobService.listExportJobs({
          project_id: projectId,
          page: 1,
          limit: 50,
        });
        const data = res.data || res;
        const jobs = data.jobs || [];
        setExportJobs(jobs);
        jobs.forEach((j) => {
          if (j.job_id && !TERMINAL_STATUSES.includes(j.status))
            pollingJobIds.current.add(j.job_id);
        });
      } catch (e) {
        if (!silent)
          toast.error(
            e.response?.data?.message || "Failed to load export jobs",
          );
      } finally {
        if (!silent) setExportLoading(false);
      }
    },
    [projectId],
  );

  const pollStatus = useCallback(async () => {
    if (!projectId || document.hidden) return;
    for (const jobId of Array.from(pollingJobIds.current)) {
      try {
        const res = await jobService.getJobStatus({
          job_id: jobId,
          project_id: projectId,
        });
        const job = res.data || res;
        if (job?.status && TERMINAL_STATUSES.includes(job.status))
          pollingJobIds.current.delete(jobId);
      } catch (_) {}
    }
    loadImportJobs(true);
    loadExportJobs(true);
  }, [projectId, loadImportJobs, loadExportJobs]);


  const loadField=async ()=>{

    const res = await fieldService.getFields({
        project_id:projectId ,
      });
      console.log({smnbdfsdf:res})
      setFields([...fields,...(res?.data?.fields || [])?.map((item)=>item.name)]);
  }
  useEffect(() => {
    if (!projectId) return;
    loadField()
    loadImportJobs();
    loadExportJobs();
  }, [projectId, loadImportJobs, loadExportJobs]);

  useEffect(() => {
    if (!projectId) return;
    let timerId;
    const schedule = () => {
      if (document.hidden) return;
      timerId = setTimeout(() => {
        pollStatus();
        schedule();
      }, POLL_INTERVAL_MS);
    };
    schedule();
    const onVisibility = () => {
      if (document.hidden && timerId) {
        clearTimeout(timerId);
        timerId = null;
      } else if (!document.hidden && !timerId) schedule();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerId) clearTimeout(timerId);
    };
  }, [projectId, pollStatus]);

  const handleStartImport = async () => {
    if (!projectId || !importFile) {
      toast.error("Select an Excel file to import");
      return;
    }
    setImportStartLoading(true);
    try {
      const mappingPayload = {};
      uploadedHeaders.forEach((header) => {
        const mapped = headerMappings[header];
        if (!mapped || mapped === "Ignore") {
          mappingPayload[header] = "__IGNORE__";
          return;
        }
        mappingPayload[header] = mapped;
      });
      const res = await jobService.startImport(
        projectId,
        importFile,
        mappingPayload,
      );
      const jobId = (res.data && res.data.job_id) || res.job_id;
      if (jobId) {
        pollingJobIds.current.add(jobId);
        toast.success("Import job queued");
        setImportFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setUploadedHeaders([]);
        setHeaderMappings({});
        loadImportJobs();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to start import");
    } finally {
      setImportStartLoading(false);
    }
  };

  const handleStartExport = async () => {
    if (!projectId) return;
    setExportStartLoading(true);
    try {
      const res = await jobService.startExport({
        project_id: projectId,
        includeDeleted: !!includeDeleted,
        includeRejected: !!includeRejected,
      });
      const jobId = (res.data && res.data.job_id) || res.job_id;
      if (jobId) {
        pollingJobIds.current.add(jobId);
        toast.success("Export job queued");
        loadExportJobs();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to start export");
    } finally {
      setExportStartLoading(false);
    }
  };

  const handleDownloadSample = async () => {
    setImportSampleLoading(true);
    try {
      const blob = await jobService.getImportSample(
        projectId ? { project_id: projectId } : {},
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "test-cases-import-sample.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Sample file downloaded");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to download sample");
    } finally {
      setImportSampleLoading(false);
    }
  };

  const parseExcelHeaders = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) return [];
      const firstSheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: "",
      });
      const row0 = Array.isArray(rows?.[0]) ? rows[0] : [];
      return row0
        .map((h) => String(h || "").trim())
        .filter((h) => h.length > 0);
    } catch (_) {
      return [];
    }
  };

  const handleImportFileChange = async (file) => {
    setImportFile(file || null);
    if (!file) {
      setUploadedHeaders([]);
      setHeaderMappings({});
      return;
    }
    const headers = await parseExcelHeaders(file);
    setUploadedHeaders(headers);
    const defaults = {};
    headers.forEach((h) => {
      const exact = IMPORT_TARGET_FIELDS.find(
        (f) => f.toLowerCase() === h.toLowerCase(),
      );
      defaults[h] = exact || "Ignore";
    });
    setHeaderMappings(defaults);
  };

  const isUniqueField = (fieldName) => {
    if (!fieldName || fieldName === "Ignore") return false;
    if (
      /^Step \d+$/i.test(fieldName) ||
      /^Expected Result \d+$/i.test(fieldName)
    )
      return false;
    return true;
  };

  const handleCancelJob = async (jobId) => {
    if (!jobId || !projectId) return;
    setCancelingJobIds((prev) => new Set(prev).add(jobId));
    try {
      await jobService.cancelJob({ job_id: jobId, project_id: projectId });
      toast.success("Cancellation requested");
      loadImportJobs();
      loadExportJobs();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to cancel job");
    } finally {
      setCancelingJobIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleDownload = async (job, type) => {
    if (!job.job_id || !projectId) return;
    try {
      const res = await jobService.getDownloadUrl({
        job_id: job.job_id,
        project_id: projectId,
        type,
      });
      const url = (res.data && res.data.url) || res.url;
      if (url) window.open(url, "_blank");
      else toast.error("No download URL");
    } catch (e) {
      toast.error(e.response?.data?.message || "Download failed");
    }
  };

  if (!projectId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 text-sm">
          Select a project to manage data import and export.
        </p>
      </div>
    );
  }

  const tabs = [
    { id: "import", label: "Import", icon: Upload },
    { id: "export", label: "Export", icon: Download },
  ];

  return (
    <div
      className="w-full max-w-full min-w-0 overflow-x-hidden"
      style={{
        height: "calc(100vh - 80px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      {/* Page header */}
      <div
        className="flex-shrink-0 flex items-center justify-between border-b"
        style={{ padding: "4px", minHeight: "64px" }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: 600,
            color: "#111827",
            margin: 0,
          }}
        >
          Data Management
        </h1>

        <div className="flex items-center" style={{ gap: "12px" }}>
          {activeTab === "import" && (
            <>
              <button
                onClick={handleDownloadSample}
                disabled={importSampleLoading}
                className="flex items-center font-medium border-none bg-[#F0F4FFFF] hover:bg-[#D9E4FFFF] transition-colors disabled:opacity-50"
                style={{
                  height: "38px",
                  padding: "0 16px",
                  borderRadius: "18px",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#2E71BDFF",
                  borderWidth: 0,
                }}
              >
                {importSampleLoading ? (
                  <InlineLoadSkeleton className="h-4 w-4" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span>Sample File</span>
              </button>
              <button
                onClick={handleStartImport}
                disabled={importStartLoading || !importFile}
                className="flex items-center font-medium border-none bg-[#EEFDF3FF] hover:bg-[#B8F5CDFF] transition-colors disabled:opacity-50"
                style={{
                  height: "38px",
                  padding: "0 16px",
                  borderRadius: "18px",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#117B34FF",
                  borderWidth: 0,
                }}
              >
                {importStartLoading ? (
                  <InlineLoadSkeleton className="h-4 w-4" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span>Start Import</span>
              </button>
            </>
          )}
          {activeTab === "export" && (
            <button
              onClick={handleStartExport}
              disabled={exportStartLoading}
              className="flex items-center font-medium border-none bg-[#FDF2F2FF] hover:bg-[#F9DBDCFF] transition-colors disabled:opacity-50"
              style={{
                height: "38px",
                padding: "0 16px",
                borderRadius: "18px",
                gap: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#DE3B40FF",
                borderWidth: 0,
              }}
            >
              {exportStartLoading ? (
                <InlineLoadSkeleton className="h-4 w-4" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>Start Export</span>
            </button>
          )}
          <button
            onClick={() => {
              loadImportJobs();
              loadExportJobs();
            }}
            className="flex items-center font-medium border-none bg-[#F9F7F5FF] hover:bg-[#EEE7E3FF] transition-colors"
            style={{
              height: "38px",
              padding: "0 16px",
              borderRadius: "18px",
              gap: "8px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#A17561FF",
              borderWidth: 0,
            }}
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center gap-1 pt-3 pb-0 border-b border-gray-200">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === id
                ? "border-[#A17561FF] text-[#A17561FF]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0 pt-4">
        {activeTab === "import" && (
          <div className="space-y-4">
            {/* Upload card */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-1">
                Upload File
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Upload an Excel file; first row must be headers. Map each column
                to a system field below before starting import.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <FileText className="w-4 h-4 text-gray-500" />
                  {importFile ? importFile.name : "Choose .xlsx / .xls file"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) =>
                      handleImportFileChange(e.target.files?.[0] || null)
                    }
                  />
                </label>
                {importFile && (
                  <span className="text-xs text-gray-400">
                    {(importFile.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            </div>

            {uploadedHeaders.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 border-b border-gray-200">
                  Column mapping
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                          Uploaded column
                        </th>
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                          Map to system field
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {uploadedHeaders.map((header) => (
                        <tr key={header}>
                          <td className="px-4 py-2 font-medium text-gray-800">
                            {header}
                          </td>
                          <td className="px-4 py-2">
                            <Select
                              size="small"
                              value={headerMappings[header] || "Ignore"}
                              onChange={(e) => {
                                const value = e.target.value;
                                setHeaderMappings((prev) => ({
                                  ...prev,
                                  [header]: value,
                                }));
                              }}
                              sx={{
                                width: '100%',
                                maxWidth: 320,
                                fontSize: '0.875rem',
                              }}
                            >
                              {fields.map((field) => {
                                const duplicateSelected =
                                  isUniqueField(field) &&
                                  Object.entries(headerMappings).some(
                                    ([h, selected]) =>
                                      h !== header && selected === field,
                                  );
                                return (
                                  <MenuItem
                                    key={field}
                                    value={field}
                                    disabled={duplicateSelected}
                                    sx={{ fontSize: '0.875rem' }}
                                  >
                                    {field}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Jobs table */}
            <JobsTable
              loading={importLoading}
              jobs={importJobs}
              emptyText="No import jobs yet."
              columns={[
                {
                  header: "Job ID",
                  render: (j) => (
                    <span className="font-mono text-xs text-gray-500">
                      {j.job_id?.slice(0, 8)}…
                    </span>
                  ),
                },
                { header: "Total", render: (j) => j.total_records ?? "—" },
                {
                  header: "Processed",
                  render: (j) => j.processed_records ?? "—",
                },
                {
                  header: "Failed",
                  render: (j) => (
                    <span
                      className={
                        j.failed_records > 0 ? "text-red-600 font-medium" : ""
                      }
                    >
                      {j.failed_records ?? "—"}
                    </span>
                  ),
                },
                {
                  header: "Imported by",
                  render: (j) =>
                    j.user_name ||
                    (j.user_id ? j.user_id.slice(0, 8) + "…" : "—"),
                },
                {
                  header: "Created",
                  render: (j) => (
                    <span className="text-xs text-gray-500">
                      {formatDate(j.created_at)}
                    </span>
                  ),
                },
                {
                  header: "Status",
                  render: (j) => <StatusBadge status={j.status} />,
                },
                {
                  header: "Actions",
                  render: (j) => (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {CANCELLABLE_STATUSES.includes(j.status) && (
                        <ActionButton
                          onClick={() => handleCancelJob(j.job_id)}
                          disabled={cancelingJobIds.has(j.job_id)}
                          color="red"
                          icon={Square}
                          spin={cancelingJobIds.has(j.job_id)}
                          label="Stop"
                        />
                      )}
                      {j.input_file_path && (
                        <ActionButton
                          onClick={() => handleDownload(j, "input")}
                          color="gray"
                          icon={Download}
                          label="Original"
                        />
                      )}
                      {j.failure_file_path && (
                        <ActionButton
                          onClick={() => handleDownload(j, "failure")}
                          color="orange"
                          icon={Download}
                          label="Failures"
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}

        {activeTab === "export" && (
          <div className="space-y-4">
            {/* Options card */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                Export Options
              </h2>
              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <Checkbox
                    checked={includeDeleted}
                    onCheckedChange={(v) => setIncludeDeleted(!!v)}
                  />
                  Include deleted test cases
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <Checkbox
                    checked={includeRejected}
                    onCheckedChange={(v) => setIncludeRejected(!!v)}
                  />
                  Include rejected test cases
                </label>
              </div>
            </div>

            {/* Jobs table */}
            <JobsTable
              loading={exportLoading}
              jobs={exportJobs}
              emptyText="No export jobs yet."
              columns={[
                {
                  header: "Job ID",
                  render: (j) => (
                    <span className="font-mono text-xs text-gray-500">
                      {j.job_id?.slice(0, 8)}…
                    </span>
                  ),
                },
                { header: "Total", render: (j) => j.total_records ?? "—" },
                {
                  header: "Exported by",
                  render: (j) =>
                    j.user_name ||
                    (j.user_id ? j.user_id.slice(0, 8) + "…" : "—"),
                },
                {
                  header: "Created",
                  render: (j) => (
                    <span className="text-xs text-gray-500">
                      {formatDate(j.created_at)}
                    </span>
                  ),
                },
                {
                  header: "Status",
                  render: (j) => <StatusBadge status={j.status} />,
                },
                {
                  header: "Processing time",
                  render: (j) => (
                    <span className="text-xs text-gray-500">
                      {processingTime(j.started_at, j.completed_at)}
                    </span>
                  ),
                },
                {
                  header: "Actions",
                  render: (j) => (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {CANCELLABLE_STATUSES.includes(j.status) && (
                        <ActionButton
                          onClick={() => handleCancelJob(j.job_id)}
                          disabled={cancelingJobIds.has(j.job_id)}
                          color="red"
                          icon={Square}
                          spin={cancelingJobIds.has(j.job_id)}
                          label="Stop"
                        />
                      )}
                      {j.status === "COMPLETED" && j.output_file_path && (
                        <ActionButton
                          onClick={() => handleDownload(j, "output")}
                          color="green"
                          icon={Download}
                          label="Download"
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Shared sub-components ── */

function JobsTable({ loading, jobs, columns, emptyText }) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 py-8">
        <TableSkeleton rows={4} columns={5} />
      </div>
    );
  }
  if (!jobs || jobs.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg flex flex-col items-center justify-center py-16 text-gray-400">
        <FileText className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((col) => (
                <th
                  key={col.header}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {jobs.map((j, i) => (
              <tr
                key={j.job_id || i}
                className="hover:bg-gray-50 transition-colors"
              >
                {columns.map((col) => (
                  <td
                    key={col.header}
                    className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap"
                  >
                    {col.render(j)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const COLOR_MAP = {
  red: {
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
    hover: "hover:bg-red-100",
  },
  green: {
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
    hover: "hover:bg-green-100",
  },
  gray: {
    bg: "bg-gray-50",
    text: "text-gray-600",
    border: "border-gray-200",
    hover: "hover:bg-gray-100",
  },
  orange: {
    bg: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-200",
    hover: "hover:bg-orange-100",
  },
};

function ActionButton({
  onClick,
  disabled,
  color = "gray",
  icon: Icon,
  spin = false,
  label,
}) {
  const c = COLOR_MAP[color] || COLOR_MAP.gray;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors disabled:opacity-50 ${c.bg} ${c.text} ${c.border} ${c.hover}`}
    >
      {spin ? (
        <Skeleton className="h-3.5 w-3.5 shrink-0 rounded" />
      ) : (
        <Icon className="w-3.5 h-3.5" />
      )}
      {label}
    </button>
  );
}
